import {Component, computed, effect, inject, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormControl, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {PackService} from '../../../services/pack.service';
import {CreatePack, PackModel, UpdatePack} from '../../../models/pack.model';
import {SpinnerComponent} from '../../spinner/spinner.component';
import {ToastService} from '../../../services/toast.service';
import {shareReplay} from 'rxjs';
import {SearchComponent, SearchSelectedEvent} from '../../shared/search/search.component';
import {IconComponent} from '../../shared/icon/icon.component';
import {EditableTextComponent, EditableTextSaveResult} from '../../shared/editable-text/editable-text.component';
import {EditableComponent, EditableSaveResult} from '../../shared/editable/editable.component';
import {PageComponent, PageFooterDirective} from '../../shared/page/page.component';
import {FormAndControls} from '../../shared/utils';
import {EntityListComponent} from '../../shared/entity-list/entity-list.component';
import {EntityCardComponent} from '../../shared/entity-card/entity-card.component';
import {MarkdownComponent} from '../../shared/markdown/markdown.component';
import {ButtonComponent} from '../../shared/button/button.component';
import {OrganizationService} from '../../../services/organization.service';

import {PackItemModel} from '../../../models/pack-item.model';
import {PromptModel, Visibility} from '../../../models/prompt.model';
import {VisibilityComponent} from '../../shared/visibility/visibility.component';
import {VisibilitySelectorComponent} from '../../shared/visibility-selector/visibility-selector.component';
import {PageTabDirective} from '../../shared/page/page-tab.directive';
import {PackUsageTabComponent} from '../pack-usage-tab/pack-usage-tab.component';
import {PackVersionModel} from '../../../models/pack-version.model';
import {NavigateService} from '../../../services/navigate.service';
import {RoleService} from '../../../services/role.service';

interface PackState {
  create?: boolean;
  loading?: boolean;
  error?: string;
}

interface PackFormDef {
  name: string;
  description: string;
  visibility: Visibility;
}

@Component({
  selector: 'app-pack-details-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SpinnerComponent,
    RouterLink,
    SearchComponent,
    IconComponent,
    EditableTextComponent,
    EditableComponent,
    PageComponent,
    PageFooterDirective,
    EntityListComponent,
    EntityCardComponent,
    MarkdownComponent,
    ButtonComponent,
    VisibilityComponent,
    VisibilitySelectorComponent,
    PageTabDirective,
    PackUsageTabComponent
  ],
  templateUrl: './pack-details-page.component.html',
  host: {
    class: 'app-container'
  }
})
export class PackDetailsPageComponent implements OnInit {
  readonly pack = signal<PackModel | null>(null);
  readonly packVersion = signal<PackVersionModel | null>(null);

  private readonly route = inject(ActivatedRoute);
  private readonly navigate = inject(NavigateService);
  private readonly packService = inject(PackService);
  private readonly roleService = inject(RoleService);
  protected readonly toastService = inject(ToastService);
  protected readonly orgService = inject(OrganizationService);
  protected readonly error = signal<string | null>(null);
  protected readonly state = signal<PackState>({});
  protected readonly items = signal<PackItemModel[]>([]);

  private oldItems: PackItemModel[] = []

  readonly previewContent = computed(() => {
    return this.packService.render(this.packVersion()!).pipe(shareReplay({ refCount: true, bufferSize: 1}))
  })

  // Computed property for edit mode based on permissions
  protected readonly editMode = computed(() => {
    const canEdit = this.roleService.canEdit();
    const creating = this.state().create;

    if (creating && !canEdit) {
      return 'never'; // No permission to create
    }

    if (creating) {
      return 'always'; // Creating new pack
    }

    // Editing existing pack - check permission
    return canEdit ? 'manual' : 'never';
  });

  protected form = this.createForm({
    name: '',
    description: '',
    visibility: 'private'
  })

  constructor() {
    effect(() => {
      this.items.set(this.packVersion()?.summary || [])
    })
  }

  private createForm(pack: PackFormDef) {
    return new FormAndControls<PackFormDef>({
      name: new FormControl(pack.name, {validators: [Validators.required], updateOn: 'change', nonNullable: true}),
      description: new FormControl(pack.description, {updateOn: 'change', nonNullable: true}),
      visibility: new FormControl(pack.visibility, {updateOn: 'change', nonNullable: true})
    })
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const name = params.get('name');
      if (name) {
        this.loadPack(name);
      } else if (this.roleService.assertCanEdit()) {
        this.updateState({create: true});
      } else {
        this.navigate.packs()
      }
    })
  }

  protected updateState(state: Partial<PackState>): void {
    this.state.update(prevState => ({...prevState, ...state}));
  }

  private loadPack(name: string): void {
    this.updateState({loading: true, error: undefined});

    this.packService.get(name).subscribe({
      next: (result) => {
        this.pack.set(result);
        this.form = this.createForm({
          name: result.name,
          description: result.description || '',
          visibility: result.visibility,
        });
        this.packVersion.set(result.latestVersion!)
      },
      error: (err) => {
        this.error.set('Failed to load pack');
        console.error('Error loading pack:', err);
      },
      complete: () => {
        this.updateState({loading: false});
      }
    });
  }

  savePack(field: keyof UpdatePack, result: EditableTextSaveResult): void
  savePack(field: keyof UpdatePack, result: EditableSaveResult, control: FormControl): void
  savePack(
    field: keyof UpdatePack,
    result: EditableTextSaveResult | EditableSaveResult,
    control?: FormControl
  ): void {
    const pack = this.pack();

    if (!pack) return;

    const request: UpdatePack = {id: pack.id};
    (request as any)[field] = ('formControl' in result) ? result.formControl.value : control!.value

    const op = this.packService.update(request);
    result.success = op;

    op.subscribe({
      next: (updatedPack) => {
        this.pack.set(updatedPack);
        this.toastService.success('Update Pack', 'Changes saved successfully!');
      },
      error: (err) => {
        this.toastService.error('Update Pack', `Failed to save changes: ${err}`);
      }
    });
  }

  createPack(): void {
    if (this.form.invalid) return;

    const formData = this.form.value;
    const request: CreatePack = {
      name: formData.name!,
      description: formData.description || undefined,
      initial_items: this.items().map(it => {
        return it instanceof PromptModel ? {prompt_id: it!.id, version: it!.version} : {
          pack_id: it!.id,
          version: it!.version
        }
      })
    }

    this.packService.create(request).subscribe({
      next: (pack) => {
        this.toastService.success('Create Pack', 'Pack created successfully!');
        this.navigate.pack(pack)
      },
      error: (err) => {
        this.toastService.error('Create Pack', `Failed to create pack: ${err.message}`);
      }
    });
  }

  addItem({item}: SearchSelectedEvent): void {
    this.items.update(prev => {
      return prev ? [...prev, item] : [item];
    });
  }

  removeItem(item: PackItemModel): void {
    this.items.update(prev => {
      return prev.filter(p => p.id !== item.id);
    });
  }

  moveItem(item: PackItemModel, direction: 'up' | 'down'): void {
    const items = Array.from(this.items())
    const index = items.indexOf(item);

    if (index >= 0) {
      if (direction === 'up' && index > 0) {
        items.splice(index, 1);
        items.splice(index - 1, 0, item);
      } else if (direction === 'down' && index < items.length - 1) {
        items.splice(index, 1);
        items.splice(index + 1, 0, item);
      }
      this.items.set(items);
    }
  }

  editItems() {
    this.oldItems = this.items()!
  }

  revertItems() {
    this.items.set(this.oldItems);
  }

  updateItems(result: EditableSaveResult) {
    const items = this.items();
    const pack = this.pack();

    if (!pack) {
      result.success = false;
      return;
    }

    if (items && items.length > 0) {
      result.success = this.toastService.handleResult(
        'Update Prompts',
        this.packService.updateItems(pack.id, items),
        pack => {
          this.pack.set(pack)
        }
      )
    } else {
      result.success = false;
    }
  }
}
